import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendsIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Remove as NoChangeIcon,
} from '@mui/icons-material';
import { getExamGradesSummary } from '../services/gradeService';
import { getExamsByClassLevel } from '../services/examService';
import { downloadStudentReport, downloadNominalRoll, bulkGenerateReports, bulkGenerateCombinedPDF, checkBulkReportStatus, getExamTrendAnalysis } from '../services/reportService';
import { ExamGradeSummary, ExamTrendAnalysis, Exam } from '../types';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import {
  Bar as BarChart,
  Pie as PieChart,
  Doughnut as DoughnutChart
} from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface ExamGradeSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  examId: number;
  examTitle?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`exam-summary-tabpanel-${index}`}
      aria-labelledby={`exam-summary-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ExamGradeSummaryDialog: React.FC<ExamGradeSummaryDialogProps> = ({
  open,
  onClose,
  examId,
  examTitle
}) => {
  const [summary, setSummary] = useState<ExamGradeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [combinedPdfLoading, setCombinedPdfLoading] = useState(false);
  const [combinedPdfMessage, setCombinedPdfMessage] = useState('');
  const [availableExams, setAvailableExams] = useState<any[]>([]);
  const [selectedComparisonExam, setSelectedComparisonExam] = useState<number | ''>('');
  const [trendData, setTrendData] = useState<ExamTrendAnalysis | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState('');

  const pollStatus = useCallback((taskId: string) => {
    const interval = setInterval(async () => {
      try {
        const statusData = await checkBulkReportStatus(taskId);
        if (statusData.status === 'completed') {
          clearInterval(interval);
          setBulkLoading(false);
          if (statusData.result.startsWith('/')) {
            setBulkMessage('Reports generated! Downloading...');
            window.location.href = `${process.env.REACT_APP_API_URL}${statusData.result}`;
          } else {
            setBulkMessage(statusData.result);
          }
        } else if (statusData.status === 'failed') {
          clearInterval(interval);
          setBulkLoading(false);
          setBulkMessage(`Failed: ${statusData.result}`);
        }
      } catch (err: any) {
        clearInterval(interval);
        setBulkLoading(false);
        setBulkMessage(err.message || 'Polling failed.');
      }
    }, 3000);
    return interval;
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getExamGradesSummary(examId);
      setSummary(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load exam summary');
    } finally {
      setLoading(false);
    }
  }, [examId]);

  const fetchAvailableExams = useCallback(async (classLevel: string, schoolId: number) => {
    try {
      const exams = await getExamsByClassLevel(classLevel, schoolId);
      setAvailableExams(exams.filter((e: any) => e.id !== examId)); // Exclude current exam
    } catch (err) {
      console.error("Failed to fetch available exams for comparison", err);
    }
  }, [examId]);

  useEffect(() => {
    if (open && examId) {
      fetchSummary();
    }
  }, [open, examId, fetchSummary]);

  useEffect(() => {
    if (summary?.class_level && summary?.school_id) {
      fetchAvailableExams(summary.class_level, summary.school_id);
    }
  }, [summary, fetchAvailableExams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleComparisonExamChange = async (event: any) => {
    const previousExamId = event.target.value as number;
    setSelectedComparisonExam(previousExamId);
    if (!previousExamId) {
      setTrendData(null);
      return;
    }

    setTrendLoading(true);
    setTrendError('');
    try {
      const data = await getExamTrendAnalysis(examId, previousExamId);
      setTrendData(data);
    } catch (err: any) {
      setTrendError((err as Error).message || 'Failed to load trend analysis');
    } finally {
      setTrendLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!summary) return;
    setBulkLoading(true);
    setBulkMessage('Starting report generation...');
    try {
      const data = await bulkGenerateReports(examId, summary.class_level);
      setBulkMessage('Processing... Please wait.');
      pollStatus(data.task_id);
    } catch (err: any) {
      setBulkLoading(false);
      setBulkMessage((err as Error).message || 'Failed to start bulk report generation.');
    }
  };

  const handleCombinedPdfGenerate = async () => {
    if (!summary) return;
    setCombinedPdfLoading(true);
    setCombinedPdfMessage('Starting combined PDF generation...');
    try {
      const data = await bulkGenerateCombinedPDF(examId, summary.class_level);
      setCombinedPdfMessage('Processing... Please wait.');
      pollStatus(data.task_id);
    } catch (err: any) {
      setCombinedPdfLoading(false);
      setCombinedPdfMessage((err as Error).message || 'Failed to start combined PDF generation.');
    }
  };

  const getGradeColor = (percentage: number): 'success' | 'info' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 65) return 'info';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  const getGradeLetter = (percentage: number) => {
    if (percentage >= 80) return 'Exceeds Expectation';
    if (percentage >= 65) return 'Meets Expectation';
    if (percentage >= 50) return 'Approaches Expectation';
    return 'Below Expectation';
  };

  // Chart data for grade distribution
  const gradeDistributionData = summary?.grade_distribution ? {
    labels: Object.keys(summary.grade_distribution),
    datasets: [{
      data: Object.values(summary.grade_distribution),
      backgroundColor: [
        '#4caf50', // Exceeds Expectation
        '#2196f3', // Meets Expectation
        '#ff9800', // Approaches Expectation
        '#f44336'  // Below Expectation
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : undefined;

  // Chart data for performance comparison
  const performanceData = summary ? {
    labels: summary.student_performances.slice(0, 10).map(s => s.student_name),
    datasets: [{
      label: 'Percentage Score',
      data: summary.student_performances.slice(0, 10).map(s => s.percentage),
      backgroundColor: 'rgba(54, 162, 235, 0.6)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  } : undefined;

  // Chart data for passing rate
  const passingRateData = summary ? {
    labels: ['Passed', 'Failed'],
    datasets: [{
      data: [summary.overall_stats.total_passed, summary.overall_stats.total_failed],
      backgroundColor: ['#4caf50', '#f44336'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : undefined;

  // Chart data for subject performance
  const subjectPerformanceData = summary && summary.subject_analysis ? {
    labels: summary.subject_analysis.map(s => s.course_title),
    datasets: [
      {
        label: 'Average Score (%)',
        data: summary.subject_analysis.map(s => (s.average_score / s.max_grade) * 100),
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1
      },
      {
        label: 'Passing Rate (%)',
        data: summary.subject_analysis.map(s => s.passing_rate),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  } : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon />
          <Typography variant="h6">
            Exam Summary: {summary?.exam_title || examTitle || 'Loading...'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : summary ? (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Overall Statistics</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-around' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4">{summary.overall_stats.average_percentage.toFixed(1)}%</Typography>
                        <Typography variant="body2" color="text.secondary">Average Score</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">{summary.overall_stats.passing_rate.toFixed(1)}%</Typography>
                        <Typography variant="body2" color="text.secondary">Passing Rate</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5">{summary.overall_stats.highest_score.toFixed(1)}%</Typography>
                        <Typography variant="body2" color="text.secondary">Highest Score</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5">{summary.overall_stats.lowest_score.toFixed(1)}%</Typography>
                        <Typography variant="body2" color="text.secondary">Lowest Score</Typography>
                    </Box>
                     <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5">{summary.total_students}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Students</Typography>
                    </Box>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="exam summary tabs">
                <Tab label="Overview" />
                <Tab label="Student Performance" />
                <Tab label="Subject Analysis" />
                <Tab label="Reports" />
                <Tab label="Trends" icon={<TrendsIcon />} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Grade Distribution</Typography>
                      {gradeDistributionData && <DoughnutChart data={gradeDistributionData} />}
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Passing Rate</Typography>
                      {passingRateData && <PieChart data={passingRateData} />}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Card>
                  <CardContent>
                     <Typography variant="h6" gutterBottom>Performance Insights</Typography>
                     <Box sx={{ display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Card>
                                <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TrophyIcon color="primary" /> Top Performers
                                </Typography>
                                <List>
                                  {summary.student_performances
                                    .sort((a, b) => b.percentage - a.percentage)
                                    .slice(0, 5)
                                    .map((student) => (
                                    <ListItem key={student.student_id}>
                                      <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: getGradeColor(student.percentage) }}>
                                          <TrophyIcon />
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText 
                                        primary={student.student_name}
                                        secondary={`${student.percentage.toFixed(1)}%`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                                </CardContent>
                            </Card>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Card>
                                <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WarningIcon color="error" /> Needs Attention
                                </Typography>
                                <List>
                                  {summary.student_performances
                                    .sort((a, b) => a.percentage - b.percentage)
                                    .slice(-5)
                                    .reverse()
                                    .map((student) => (
                                    <ListItem key={student.student_id}>
                                      <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'error.main' }}>
                                          <WarningIcon />
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText 
                                        primary={student.student_name}
                                        secondary={`${student.percentage.toFixed(1)}%`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                                </CardContent>
                            </Card>
                        </Box>
                     </Box>
                  </CardContent>
                </Card>
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Student Performance</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Rank</TableCell>
                                    <TableCell>Student ID</TableCell>
                                    <TableCell>Student Name</TableCell>
                                    <TableCell>Score</TableCell>
                                    <TableCell>Percentage</TableCell>
                                    <TableCell>Grade</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Report</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {summary.student_performances.sort((a, b) => a.rank - b.rank).map(student => (
                                    <TableRow key={student.student_id}>
                                        <TableCell>{student.rank}</TableCell>
                                        <TableCell>{student.student_code}</TableCell>
                                        <TableCell>{student.student_name}</TableCell>
                                        <TableCell>{student.score.toFixed(1)}</TableCell>
                                        <TableCell>{student.percentage.toFixed(1)}%</TableCell>
                                        <TableCell>
                                            <Chip 
                                              label={getGradeLetter(student.percentage)}
                                              color={getGradeColor(student.percentage)}
                                              size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                icon={student.status === 'passed' ? <CheckCircleIcon /> : <CancelIcon />}
                                                label={student.status}
                                                color={student.status === 'passed' ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Download Report">
                                              <IconButton onClick={() => downloadStudentReport(examId, student.student_id)}>
                                                <DownloadIcon />
                                              </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Subject Analysis</Typography>
                  <Box sx={{ height: 400 }}>
                    {subjectPerformanceData && <BarChart 
                        data={subjectPerformanceData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    max: 100
                                }
                            }
                        }}
                    />}
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Reports</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadNominalRoll(examId, summary.class_level)}
                    >
                      Download Nominal Roll
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary"
                        startIcon={<DownloadIcon />}
                        onClick={handleBulkGenerate}
                        disabled={bulkLoading}
                    >
                      {bulkLoading ? <CircularProgress size={24} /> : 'Generate All Student Reports (ZIP)'}
                    </Button>
                            {/* Combined PDF button disabled temporarily
        <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleCombinedPdfGenerate}
            disabled={combinedPdfLoading}
        >
          {combinedPdfLoading ? <CircularProgress size={24} /> : 'Generate Combined PDF'}
        </Button>
        */}
                    {bulkMessage && <Alert severity={bulkMessage.startsWith('Failed') ? 'error' : 'info'}>{bulkMessage}</Alert>}
                    {combinedPdfMessage && <Alert severity={combinedPdfMessage.startsWith('Failed') ? 'error' : 'info'}>{combinedPdfMessage}</Alert>}
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>
            
            <TabPanel value={tabValue} index={4}>
              <Box>
                <Typography variant="h6" gutterBottom>Trend Analysis</Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="comparison-exam-select-label">Select Exam to Compare</InputLabel>
                  <Select
                    labelId="comparison-exam-select-label"
                    value={selectedComparisonExam}
                    label="Select Exam to Compare"
                    onChange={handleComparisonExamChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {availableExams.map((exam) => (
                      <MenuItem key={exam.id} value={exam.id}>
                        {exam.title} ({new Date(exam.exam_date).toLocaleDateString()})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {trendLoading && <CircularProgress />}
                {trendError && <Alert severity="error">{trendError}</Alert>}
                {trendData && <TrendComparisonDisplay trendData={trendData} />}
              </Box>
            </TabPanel>

          </Box>
        ) : (
          <Typography>No exam summary data available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const TrendComparisonDisplay: React.FC<{ trendData: ExamTrendAnalysis }> = ({ trendData }) => {
  const { current_exam_summary, previous_exam_summary, comparison } = trendData;

  const renderTrend = (value: number) => {
    if (value > 0.1) return <ArrowUpwardIcon color="success" sx={{ verticalAlign: 'middle' }} />;
    if (value < -0.1) return <ArrowDownwardIcon color="error" sx={{ verticalAlign: 'middle' }} />;
    return <NoChangeIcon color="disabled" sx={{ verticalAlign: 'middle' }} />;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Comparison: {current_exam_summary.exam_title} vs. {previous_exam_summary.exam_title}
      </Typography>

      {/* Overall Stats */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">Overall Statistics</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align="right">{previous_exam_summary.exam_title}</TableCell>
                <TableCell align="right">{current_exam_summary.exam_title}</TableCell>
                <TableCell align="right">Trend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Average Score (%)</TableCell>
                <TableCell align="right">{previous_exam_summary.overall_stats.average_percentage.toFixed(1)}%</TableCell>
                <TableCell align="right">{current_exam_summary.overall_stats.average_percentage.toFixed(1)}%</TableCell>
                <TableCell align="right">
                  {renderTrend(comparison.overall_stats_diff.average_percentage_diff)}
                  ({comparison.overall_stats_diff.average_percentage_diff.toFixed(1)}%)
                </TableCell>
              </TableRow>
               <TableRow>
                <TableCell>Passing Rate (%)</TableCell>
                <TableCell align="right">{previous_exam_summary.overall_stats.passing_rate.toFixed(1)}%</TableCell>
                <TableCell align="right">{current_exam_summary.overall_stats.passing_rate.toFixed(1)}%</TableCell>
                <TableCell align="right">
                  {renderTrend(comparison.overall_stats_diff.passing_rate_diff)}
                  ({comparison.overall_stats_diff.passing_rate_diff.toFixed(1)}%)
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Subject Performance */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">Subject Performance</Typography>
           <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell align="right">Avg. Score Change</TableCell>
                  <TableCell align="right">Passing Rate Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparison.subject_performance_diff.map((diff) => (
                  <TableRow key={diff.course_id}>
                    <TableCell>{diff.course_title}</TableCell>
                    <TableCell align="right">
                       {renderTrend(diff.average_score_diff)}
                       {diff.average_score_diff.toFixed(1)}
                    </TableCell>
                    <TableCell align="right">
                      {renderTrend(diff.passing_rate_diff)}
                      {diff.passing_rate_diff.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
           </TableContainer>
        </CardContent>
      </Card>

    </Box>
  );
};

export default ExamGradeSummaryDialog; 